import React, { useCallback, useMemo } from 'react'
import { Box, Card, CardContent, MenuItem, Paper, useTheme } from '@mui/material'
import ReactECharts from 'echarts-for-react'
import { type KeyStatSurveyItem } from '../../../../../models/Report'
import { formatTooltip, formatSeriesDisplayName, getFilteredOptions, buildBarSeries, getBarChartColors, calculateChartHeight, shouldRenderSliceBaselineReferenceLine } from './helper'
import type { OptionType } from './helper'
import { useReportsContext } from '../../../ReportsContext'
import { getChartDisplayOptions } from '../../questionSampleSize'
import { responseFilterBaselineColor } from '../../constants'

const BASELINE_REFERENCE_LINE_WIDTH = 9

interface StableEChartsProps {
  chartInstanceKey: string
  chartHeight: number
  chartEvents: Record<string, (params: any) => void>
  optionsData: any
}

const StableECharts = React.memo(({
  chartInstanceKey,
  chartHeight,
  chartEvents,
  optionsData
}: StableEChartsProps) => (
  <ReactECharts
    key={chartInstanceKey}
    option={optionsData}
    style={{ width: '100%', height: `${chartHeight}px` }}
    notMerge
    lazyUpdate
    opts={{ renderer: 'svg' }}
    onEvents={chartEvents}
  />
), (prevProps, nextProps) => (
  prevProps.chartInstanceKey === nextProps.chartInstanceKey &&
  prevProps.chartHeight === nextProps.chartHeight &&
  prevProps.chartEvents === nextProps.chartEvents &&
  prevProps.optionsData === nextProps.optionsData
))

StableECharts.displayName = 'StableECharts'

interface ChartContextMenuState {
  top: number
  left: number
  answer: string
}

export interface SurveyResponseMenuSelection {
  itemId: string
  revision: number
  prompt: string
  questionNumber: number
  sectionTitle: string
  answer: string
  count: number
  surveySectionId?: string
  surveySectionRevision?: number
  instructorId?: string | null
}

export interface BarChartProps {
  titleComponent: React.ReactNode
  statisticBox: React.ReactNode
  chartKey: number
  showPercents?: boolean
  titles?: string[]
  report?: KeyStatSurveyItem
  showSlice?: boolean
  isHorizontalLayout?: boolean
  questionNumber: number
  sectionTitle: string
  surveySectionId?: string
  surveySectionRevision?: number
  instructorId?: string | null
  onFilterBySurveyResponse?: (selection: SurveyResponseMenuSelection) => void | Promise<void>
  onRemoveResponseFilterAnswer?: (answer: string) => void | Promise<void>
  highlightedResponseAnswers?: string[]
}

const buildCustomLineSeries = (titles: string[], options: OptionType[], barColors: string[]): any => {
  const customData = options.map((item, i) => {
    const percent = item.percents?.[0] ?? 0
    return [i, percent, `${percent}%`]
  })
  return {
    type: 'custom',
    name: titles[0],
    renderItem: (params: any, api: any) => {
      const xIndex = api.value(0)
      const yValue = api.value(1)
      const labelText = api.value(2)
      if (!yValue) return
      const xCenter = api.coord([xIndex, 0])[0]
      const yCoord = api.coord([0, yValue])[1]
      const barWidth = api.size([1, 0])[0]
      const xStart = Number(xCenter) - Number(barWidth) / 2
      const xEnd = Number(xCenter) + Number(barWidth) / 2
      return {
        type: 'group',
        children: [
          {
            type: 'line',
            shape: {
              x1: xStart + 25,
              y1: yCoord,
              x2: xEnd - 25,
              y2: yCoord
            },
            style: {
              stroke: barColors[0],
              // Keep the baseline visibly distinct from the comparison bars,
              // but thin enough that it reads as a reference line instead of a bar.
              lineWidth: BASELINE_REFERENCE_LINE_WIDTH
            }
          },
          {
            type: 'text',
            style: {
              text: labelText,
              x: xStart + 10,
              y: Number(yCoord) + 7,
              fill: barColors[0],
              font: '14px',
              fontWeight: '600',
              textAlign: 'center',
              textVerticalAlign: 'bottom'
            }
          }
        ]
      }
    },
    data: customData,
    z: 10
  }
}

const BarChart: React.FC<BarChartProps> = ({
  titleComponent,
  statisticBox,
  chartKey,
  showPercents = false,
  titles = [],
  report,
  showSlice = false,
  isHorizontalLayout,
  questionNumber,
  sectionTitle,
  surveySectionId,
  surveySectionRevision,
  instructorId,
  onFilterBySurveyResponse,
  onRemoveResponseFilterAnswer,
  highlightedResponseAnswers = []
}) => {
  const theme = useTheme()
  const { selectedTrend } = useReportsContext()
  const [contextMenuPosition, setContextMenuPosition] = React.useState<ChartContextMenuState | null>(null)
  const hoverRegionRef = React.useRef<HTMLDivElement | null>(null)
  const contextMenuRef = React.useRef<HTMLDivElement | null>(null)

  const options = useMemo<OptionType[]>(() => getChartDisplayOptions(report), [report])
  const keys = useMemo(() => options.map(option => option.name), [options])
  const useHorizontalLayout = typeof isHorizontalLayout === 'boolean' ? isHorizontalLayout : keys.length > 8
  const maxCategoryLabelLength = keys.reduce((max, key) => Math.max(max, String(key).length), 0)
  const horizontalYAxisLabelWidth = Math.min(190, Math.max(100, maxCategoryLabelLength * 8))
  const horizontalGridLeft = horizontalYAxisLabelWidth + 24
  const horizontalGridRight = showPercents ? 48 : 44
  const groupCount = options[0]?.counts?.length ?? 1
  const hasLegend = groupCount > 1
  const chartHeight = calculateChartHeight(keys.length, useHorizontalLayout, hasLegend)

  const optionsData = useMemo(() => {
    const groupCount = options[0]?.counts?.length ?? 1
    const renderBaselineReferenceLine = shouldRenderSliceBaselineReferenceLine(showSlice, useHorizontalLayout)
    const filteredOptions = getFilteredOptions(options, renderBaselineReferenceLine)
    const series = buildBarSeries({
      groupCount,
      titles,
      filteredOptions,
      showPercents,
      theme,
      omitBaselineFromBars: renderBaselineReferenceLine,
      barColors: getBarChartColors(selectedTrend),
      isHorizontal: useHorizontalLayout,
      highlightedResponseAnswers,
      mutedBarColor: responseFilterBaselineColor
    })
    const customLineSeries = buildCustomLineSeries(titles, options, getBarChartColors(selectedTrend))

    // Calculate bottom spacing - need more space when legend is shown
    const hasLegend = groupCount > 1
    const baseBottom = useHorizontalLayout ? 30 : 60
    const legendBottom = hasLegend ? (useHorizontalLayout ? 70 : 80) : baseBottom
    const finalBottom = useHorizontalLayout
      ? (groupCount > 3 ? Math.max(90, legendBottom) : legendBottom)
      : (groupCount > 3 ? Math.max(100, legendBottom) : legendBottom)

    return {
      color: getBarChartColors(selectedTrend),
      grid: useHorizontalLayout
        ? { left: horizontalGridLeft, right: horizontalGridRight, top: 36, bottom: finalBottom }
        : { left: 60, right: 20, top: 70, bottom: finalBottom },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) =>
          formatTooltip({ params, titles, keys, options, showPercents })
      },
      legend: {
        show: hasLegend,
        bottom: 20,
        type: 'plain',
        itemWidth: 14,
        itemHeight: 14,
        data: titles,
        itemGap: 40,
        textStyle: {
          color: theme.palette.text.secondary,
          fontSize: 14
        },
        formatter: (name: string) => {
          const displayName = formatSeriesDisplayName(name, titles)
          // Truncate long legend names to prevent overlap
          // Allow more characters for horizontal charts, less for vertical
          const maxLength = useHorizontalLayout ? 30 : 20
          if (displayName.length > maxLength) {
            return displayName.substring(0, maxLength - 3) + '...'
          }
          return displayName
        },
        width: 'auto',
        orient: 'horizontal'
      },
      xAxis: useHorizontalLayout
        ? {
            type: 'value',
            min: 0,
            max: showPercents ? 100 : undefined,
            splitLine: { show: true },
            axisLabel: {
              show: showPercents,
              formatter: '{value}%',
              color: theme.palette.text.secondary,
              fontSize: 14,
              margin: 14
            },
            axisLine: { show: false },
            axisTick: { show: false }
          }
        : {
            type: 'category',
            data: keys,
            position: 'top',
            axisLabel: {
              color: theme.palette.text.secondary,
              fontSize: 14,
              fontWeight: 500,
              align: 'center',
              interval: 0,
              margin: 40,
              overflow: 'truncate',
              width: 110,
              showMaxLabel: true,
              showMinLabel: true
            },
            axisTick: { show: false },
            axisLine: { show: false },
            z: 10
          },
      yAxis: useHorizontalLayout
        ? {
            type: 'category',
            data: keys,
            axisLine: { show: false },
            axisLabel: {
              show: true,
              color: theme.palette.text.secondary,
              fontSize: 14,
              fontWeight: 500,
              margin: 12,
              overflow: 'truncate',
              width: horizontalYAxisLabelWidth
            },
            axisTick: { show: false },
            splitLine: { show: false }
          }
        : {
            type: 'value',
            min: 0,
            max: showPercents ? 100 : undefined,
            splitLine: { show: true },
            axisLabel: {
              show: showPercents,
              formatter: '{value}%',
              color: theme.palette.text.secondary,
              fontSize: 14,
              margin: 20
            },
            axisLine: { show: false },
            axisTick: { show: false }
          },
      series: [
        ...(renderBaselineReferenceLine ? [customLineSeries] : []),
        ...series.map(s => ({
          ...s,
          emphasis: {
            disabled: true
          },
          select: {
            disabled: true
          },
          barCategoryGap: useHorizontalLayout ? 20 : 50,
          barGap: '0%'
        }))
      ]
    }
  }, [report, titles, showPercents, theme, showSlice, selectedTrend, options, keys, useHorizontalLayout, horizontalGridLeft, horizontalGridRight, horizontalYAxisLabelWidth, highlightedResponseAnswers])

  const handleCloseContextMenu = useCallback((): void => {
    setContextMenuPosition(null)
  }, [])

  const handleBarClick = useCallback((params: any): void => {
    if (!onFilterBySurveyResponse && highlightedResponseAnswers.length === 0) {
      return
    }

    if (params?.componentType !== 'series' || params?.seriesType !== 'bar') {
      handleCloseContextMenu()
      return
    }

    const nativeEvent = params?.event?.event
    const mouseX = nativeEvent?.clientX
    const mouseY = nativeEvent?.clientY
    const hoverRegionBounds = hoverRegionRef.current?.getBoundingClientRect()

    if (
      typeof mouseX !== 'number' ||
      typeof mouseY !== 'number' ||
      !hoverRegionBounds ||
      typeof params?.name !== 'string' ||
      params.name.trim() === ''
    ) {
      handleCloseContextMenu()
      return
    }

    setContextMenuPosition({
      top: mouseY - hoverRegionBounds.top + 8,
      left: mouseX - hoverRegionBounds.left + 8,
      answer: params.name
    })
  }, [handleCloseContextMenu, onFilterBySurveyResponse, highlightedResponseAnswers])

  const isSelectedAnswer = contextMenuPosition != null && highlightedResponseAnswers.includes(contextMenuPosition.answer)

  const chartEvents = useMemo(() => ({
    click: handleBarClick
  }), [handleBarClick])

  const highlightedAnswersKey = highlightedResponseAnswers.join('|')
  const chartInstanceKey = `${chartKey}-${showPercents ? 'pct' : 'raw'}-${useHorizontalLayout ? 'horizontal' : 'vertical'}-${highlightedAnswersKey}`

  React.useEffect(() => {
    if (!contextMenuPosition) {
      return
    }

    const handleDocumentMouseDown = (event: MouseEvent): void => {
      const targetNode = event.target as Node | null

      if (targetNode && contextMenuRef.current?.contains(targetNode)) {
        return
      }

      handleCloseContextMenu()
    }

    document.addEventListener('mousedown', handleDocumentMouseDown)

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown)
    }
  }, [contextMenuPosition])

  React.useEffect(() => {
    setContextMenuPosition(null)
  }, [report, highlightedAnswersKey])

  return (
    <Card key={chartKey} sx={{ overflow: 'visible' }}>
      <CardContent sx={{ overflow: 'visible' }}>
        {titleComponent}
        <Box
          data-testid="bar-chart-hover-region"
          ref={hoverRegionRef}
          sx={{ position: 'relative' }}
        >
          <StableECharts
            chartInstanceKey={chartInstanceKey}
            chartHeight={chartHeight}
            chartEvents={chartEvents}
            optionsData={optionsData}
          />
          {contextMenuPosition && (
            <Paper
              elevation={6}
              ref={contextMenuRef}
              sx={{
                position: 'absolute',
                top: contextMenuPosition.top,
                left: contextMenuPosition.left,
                zIndex: 6
              }}
            >
              <MenuItem onClick={() => {
                if (contextMenuPosition.answer && isSelectedAnswer) {
                  void onRemoveResponseFilterAnswer?.(contextMenuPosition.answer)
                } else if (contextMenuPosition.answer && report) {
                  void onFilterBySurveyResponse?.({
                    itemId: report.itemId,
                    revision: report.revision,
                    prompt: report.prompt,
                    questionNumber,
                    sectionTitle,
                    answer: contextMenuPosition.answer,
                    count: report.options.find((option) => option.name === contextMenuPosition.answer)?.counts?.[0] ?? 0,
                    surveySectionId,
                    surveySectionRevision,
                    instructorId
                  })
                }
                handleCloseContextMenu()
              }}>
                {isSelectedAnswer ? 'Remove Filter' : 'Filter by Survey Response'}
              </MenuItem>
            </Paper>
          )}
        </Box>
        {statisticBox}
      </CardContent>
    </Card>
  )
}

export default BarChart
